import {
  add,
  differenceInCalendarDays,
  differenceInHours,
  endOfDay,
  endOfMonth,
  format,
  isWeekend,
  startOfDay,
  startOfMonth,
  sub,
} from 'date-fns';
import { isEmpty } from 'lodash';

import { TIMELINE_SCALES } from '@/lib/constants';
import { TimelineItemModel } from '@/lib/models/timeline-item/types';
import { TimelineRange, TimelineScaleTypes } from '@/types';
import { formatIsoDate } from '@/utils/format/date-time';

export const getTimelineRange = (items: TimelineItemModel[], currentDate: Date): TimelineRange => {
  const itemsWithDates = items.filter((item) => item.startDate);

  if (isEmpty(itemsWithDates)) {
    const startOfRange = startOfMonth(sub(currentDate, { weeks: 2 }));
    const endOfRange = endOfMonth(add(currentDate, { weeks: 2 }));
    const daysBetween = getDaysBetween(startOfRange, endOfRange);

    return {
      start: startOfRange,
      end: endOfRange,
      days: daysBetween,
    };
  }

  const itemsByStartDate = [...itemsWithDates].sort((a, b) => {
    return new Date(a.startDate).valueOf() - new Date(b.startDate).valueOf();
  });

  const itemsByEndDate = [...itemsWithDates].sort((a, b) => {
    return new Date(a.endDate).valueOf() - new Date(b.endDate).valueOf();
  });

  const firstItem = itemsByStartDate[0];
  const lastItem = itemsByEndDate[itemsByEndDate.length - 1];

  const minDate = startOfDay(new Date(firstItem.startDate));
  const maxDate = endOfDay(new Date(lastItem.endDate));
  const startOfRange = startOfMonth(sub(minDate, { weeks: 1 }));
  const endOfRange = endOfMonth(add(maxDate, { weeks: 1 }));
  const daysBetween = getDaysBetween(startOfRange, endOfRange);

  return {
    start: startOfRange,
    end: endOfRange,
    days: daysBetween,
  };
};

export const getDaysBetween = (start: Date, end: Date, exact = true) => {
  if (exact) {
    return differenceInCalendarDays(end, start);
  }
  // Calculates difference in hours and returns fractional days count
  const hours = differenceInHours(end, start);
  return hours / 24;
};

export const buildTimelineRangebar = (range: TimelineRange, scale: TimelineScaleTypes) => {
  const { start, days } = range;
  let prevMonth = '';

  return [...Array(days)].map((_, i) => {
    const thisDay = add(start, { days: i });
    const thisDayOfWeek = format(thisDay, 'EEEEEE');
    const thisMonth = format(thisDay, 'MM');
    const monthLabel = format(thisDay, 'MMM yyyy');
    const dayLabel = format(thisDay, 'd');
    const isStartOfMonth = thisMonth !== prevMonth;

    prevMonth = thisMonth;

    return {
      key: formatIsoDate(thisDay),
      monthLabel: isStartOfMonth ? monthLabel : '',
      dayLabel,
      hasGridLine: shouldShowGridLine(scale, isStartOfMonth),
      isWeekend: isWeekend(thisDay),
      shouldShowDayLabel: shouldShowDayLabel(scale, thisDayOfWeek),
    };
  });
};

const shouldShowGridLine = (scale: TimelineScaleTypes, isStartOfMonth: boolean) => {
  switch (scale) {
    case TIMELINE_SCALES.MONTH:
    case TIMELINE_SCALES.WEEK:
      return true;
    default:
      return isStartOfMonth;
  }
};

const shouldShowDayLabel = (scale: TimelineScaleTypes, thisDayOfWeek: string) => {
  switch (scale) {
    case TIMELINE_SCALES.QUARTER:
    case TIMELINE_SCALES.MONTH:
      return thisDayOfWeek === 'Mo';
    case TIMELINE_SCALES.WEEK:
      return true;
    default:
      return false;
  }
};

export const getTimelineGridSize = (scale: TimelineScaleTypes): number => {
  switch (scale) {
    case TIMELINE_SCALES.MONTH:
      return 16;
    case TIMELINE_SCALES.WEEK:
      return 24;
    default:
      return 8;
  }
};
